import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Skeleton,
    Typography,
    useTheme,
} from '@mui/material';
import {
    AscSort,
    CustomTableContextMenuItem,
    DContextMenu,
    DContextMenuItem,
    DescSort,
    DPaginator,
    DTextField,
    FilterIcon,
    handleMouseEvent,
    useDContextMenu,
} from '../../index';
import {
    handleTouchDoubleTap,
    isInteractiveTarget,
    TouchDoubleTapState,
    useString,
} from '../../../../helpers';

export interface ICardListSort<F extends string> {
    field: F;
    order: 'asc' | 'desc';
}

/**
 * Sort state for a CCardList, typed by the GraphQL sortable-fields enum and
 * persisted to localStorage (same convention as useNumber/useBoolean filters).
 * `toggle` cycles a field asc → desc → off; picking another field restarts at
 * asc. Exactly one field can be active at a time.
 */
export function useCardListSort<F extends string>(storageKey: string) {
    const [raw, setRaw] = useString({ KEY: storageKey + '_Sort' });

    const sort = useMemo<ICardListSort<F> | null>(() => {
        if (!raw) {
            return null;
        }
        const [field, order] = raw.split(':');
        if (!field || (order !== 'asc' && order !== 'desc')) {
            return null;
        }
        return { field: field as F, order };
    }, [raw]);

    const setSort = useCallback(
        (next: ICardListSort<F> | null) => {
            setRaw(next ? `${next.field}:${next.order}` : '');
        },
        [setRaw],
    );

    const toggle = useCallback(
        (field: F) => {
            if (!sort || sort.field !== field) {
                setSort({ field, order: 'asc' });
            } else if (sort.order === 'asc') {
                setSort({ field, order: 'desc' });
            } else {
                setSort(null);
            }
        },
        [sort, setSort],
    );

    return { sort, setSort, toggle };
}

export interface ICardListProps<T, F extends string = string> {
    title?: string;

    // Data + pagination. The paginator renders inside the toolbar.
    data: T[];
    // Stable React key per item (use the entity id, not the array index, so
    // cards don't get recycled across pages).
    keyOf: (item: T, index: number) => React.Key;
    // Each item renders as a custom card; CCardList only provides the frame
    // (stripe background, border, click + context-menu wiring).
    renderItem: (item: T, index: number) => React.ReactNode;
    skip: number;
    take: number;
    count?: number;
    setSkip: (skip: number) => void;

    // General (debounced upstream) text filter.
    filterValue?: string;
    onFilterChange?: (value: string) => void;
    filterHelperText?: string;

    // Extra filter controls. Drop them in as plain children — the container
    // handles wrapping and overrides the 25rem minWidth that DTextField /
    // DAutocomplete hardcode, so callers don't repeat per-filter Box wrappers.
    // On medium screens and below these collapse behind a "Filtros" toggle so
    // they don't add a permanent row.
    filters?: React.ReactNode;
    // Optional count of currently-active extra filters, shown as a badge on the
    // "Filtros" toggle on medium screens. Callers opt in (the container can't
    // introspect the opaque `filters` children to count them).
    activeFilterCount?: number;

    // Typed sorting: F is the generated sortable-fields enum, so these chips
    // and the query variables share one vocabulary (no index mapping).
    sortOptions?: { label: string; field: F }[];
    sort?: ICardListSort<F> | null;
    onSortToggle?: (field: F) => void;

    onItemDoubleClick?: (item: T) => void;
    contextItems?: CustomTableContextMenuItem<T>[];
    emptyText?: string;
    // When true and there's no data yet, render placeholder cards instead of the
    // empty text — so the first load reserves the list shape. Skeletons only show
    // on the initial fetch (paging keeps the prior cards while data is non-empty).
    loading?: boolean;
    // Cap each card's width and center it, so rows stay a readable length on
    // wide screens. Off by default (cards fill the container width).
    constrainCardWidth?: boolean;
}

export function CCardList<T, F extends string = string>({
    title,
    data,
    keyOf,
    renderItem,
    skip,
    take,
    count,
    setSkip,
    filterValue,
    onFilterChange,
    filterHelperText,
    filters,
    activeFilterCount,
    sortOptions,
    sort,
    onSortToggle,
    onItemDoubleClick,
    contextItems,
    emptyText = 'No hay resultados',
    constrainCardWidth = false,
    loading = false,
}: ICardListProps<T, F>) {
    const theme = useTheme();
    const stripeColor =
        theme.palette.mode === 'dark' ? '#303030' : '#F5F5F5';
    const [filtersOpen, setFiltersOpen] = useState(false);
    const hasActiveFilters = !!activeFilterCount && activeFilterCount > 0;

    // Shared layout for the extra-filter controls, shown inside the
    // "Filtros" collapse at every screen size.
    const filtersRowSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        gap: 1,
        '& .MuiTextField-root': {
            minWidth: 0,
            width: 220,
        },
        // DCheckbox's root FormGroup is width:100% — keep it shrink-to-fit.
        '& .MuiFormGroup-root': {
            width: 'auto',
        },
    } as const;

    const [contextMenu, setContextMenu] = useDContextMenu();
    const clickedItem = useRef<T | null>(null);
    const touchDoubleTap = useRef<TouchDoubleTapState | null>(null);

    const contextMenuItems: DContextMenuItem[] = useMemo(() => {
        if (!contextItems) {
            return [];
        }
        return contextItems.map((item) => {
            return {
                ...item,
                callback: () => {
                    if (clickedItem.current) {
                        item.callback(clickedItem.current);
                    }
                },
                disable: () => {
                    return item.disable && clickedItem.current
                        ? item.disable(clickedItem.current)
                        : false;
                },
            };
        });
    }, [contextItems]);

    return (
        // Fill the DBlock height and anchor the flex column (minHeight: 0) so
        // the card list scrolls internally instead of overflowing the page.
        // The toolbar (title / filter / paginator / sort) stays fixed at top.
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                flex: 1,
            }}
        >
            {title ? (
                <Typography
                    variant={'subtitle1'}
                    fontWeight={'bold'}
                    sx={{ mb: 1 }}
                >
                    {title}
                </Typography>
            ) : null}
            {/* One wrapping toolbar row instead of separate filter/paginator,
                extra-filters and sort rows — same controls, grouped so they
                share horizontal space and only spill onto another line when
                they don't fit. Extra filters always sit behind the "Filtros"
                toggle (not just on medium/small) so they never force their own
                permanent row; the pool of filters/sort fields is unchanged. */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: filters ? 1 : 1.5,
                }}
            >
                {onFilterChange ? (
                    // Preferred width via flex-basis (25rem) but minWidth:0 so it
                    // always shrinks instead of overflowing when space is tight
                    // (DTextField no longer carries a hidden 400px floor). On
                    // mobile the basis of 100% gives it its own full-width row.
                    <Box
                        sx={{
                            flex: { xs: '1 1 100%', md: '1 1 25rem' },
                            minWidth: 0,
                        }}
                    >
                        <DTextField
                            label={'Filtro'}
                            helperText={filterHelperText}
                            value={filterValue ?? ''}
                            onChange={onFilterChange}
                            hideHelperTextBelowLg
                        />
                    </Box>
                ) : null}
                {filters ? (
                    // When any extra filter is active the button turns filled +
                    // primary and shows the count, so users can tell filters are
                    // applied without opening the panel. Otherwise it's a plain
                    // outlined toggle.
                    <Button
                        size={'small'}
                        variant={hasActiveFilters ? 'contained' : 'outlined'}
                        color={hasActiveFilters ? 'primary' : 'inherit'}
                        startIcon={<FilterIcon />}
                        onClick={() => setFiltersOpen((open) => !open)}
                        sx={{ textTransform: 'none', flexShrink: 0 }}
                    >
                        {hasActiveFilters
                            ? `Filtros (${activeFilterCount})`
                            : 'Filtros'}
                    </Button>
                ) : null}
                {sortOptions && sortOptions.length > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        <Typography variant={'caption'} color={'text.secondary'}>
                            Ordenar:
                        </Typography>
                        {sortOptions.map((option) => {
                            const active = sort?.field === option.field;
                            return (
                                <Chip
                                    key={option.field}
                                    size={'small'}
                                    label={option.label}
                                    color={active ? 'primary' : 'default'}
                                    variant={active ? 'filled' : 'outlined'}
                                    icon={
                                        active ? (
                                            sort!.order === 'asc' ? (
                                                <AscSort fontSize={'small'} />
                                            ) : (
                                                <DescSort fontSize={'small'} />
                                            )
                                        ) : undefined
                                    }
                                    onClick={
                                        onSortToggle
                                            ? () => onSortToggle(option.field)
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </Box>
                ) : null}
                <Box sx={{ ml: { xs: 0, md: 'auto' } }}>
                    <DPaginator
                        skip={skip}
                        setSkip={setSkip}
                        take={take}
                        count={count}
                        disableGutters
                    />
                </Box>
            </Box>
            {filters ? (
                <Collapse in={filtersOpen}>
                    <Box sx={{ ...filtersRowSx, mb: 1.5 }}>{filters}</Box>
                </Collapse>
            ) : null}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {loading && data.length === 0 ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                bgcolor:
                                    index % 2 === 1
                                        ? stripeColor
                                        : 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                mb: 1.5,
                                ...(constrainCardWidth
                                    ? { maxWidth: 1100, mx: 'auto' }
                                    : {}),
                            }}
                        >
                            <Skeleton variant={'text'} width={'40%'} />
                            <Skeleton variant={'text'} width={'70%'} />
                            <Skeleton variant={'text'} width={'55%'} />
                        </Box>
                    ))
                ) : data.length > 0 ? (
                    data.map((item, index) => {
                        return (
                            <Box
                                key={keyOf(item, index)}
                                onDoubleClick={
                                    onItemDoubleClick
                                        ? (e: React.MouseEvent) => {
                                              if (
                                                  e.currentTarget instanceof
                                                      HTMLElement &&
                                                  isInteractiveTarget(
                                                      e,
                                                      e.currentTarget,
                                                  )
                                              )
                                                  return;
                                              onItemDoubleClick(item);
                                          }
                                        : undefined
                                }
                                onTouchEnd={
                                    onItemDoubleClick
                                        ? (e: React.TouchEvent<HTMLElement>) => {
                                              handleTouchDoubleTap(
                                                  e,
                                                  touchDoubleTap,
                                                  () => onItemDoubleClick(item),
                                                  String(keyOf(item, index)),
                                              );
                                          }
                                        : undefined
                                }
                                onKeyDown={
                                    onItemDoubleClick
                                        ? (e: React.KeyboardEvent) => {
                                              if (e.key !== 'Enter') return;
                                              if (
                                                  e.currentTarget instanceof
                                                      HTMLElement &&
                                                  isInteractiveTarget(
                                                      e,
                                                      e.currentTarget,
                                                  )
                                              )
                                                  return;
                                              e.preventDefault();
                                              onItemDoubleClick(item);
                                          }
                                        : undefined
                                }
                                tabIndex={onItemDoubleClick ? 0 : undefined}
                                title={
                                    onItemDoubleClick
                                        ? 'Doble clic o doble toque para abrir'
                                        : undefined
                                }
                                onContextMenu={
                                    contextItems
                                        ? (e: React.MouseEvent) => {
                                              handleMouseEvent(
                                                  e,
                                                  setContextMenu,
                                                  contextMenu,
                                              );
                                              clickedItem.current = item;
                                          }
                                        : undefined
                                }
                                sx={{
                                    bgcolor:
                                        index % 2 === 1
                                            ? stripeColor
                                            : 'background.paper',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    mb: 1.5,
                                    ...(constrainCardWidth
                                        ? { maxWidth: 1100, mx: 'auto' }
                                        : {}),
                                    cursor: onItemDoubleClick
                                        ? 'pointer'
                                        : 'default',
                                    touchAction: onItemDoubleClick
                                        ? 'manipulation'
                                        : undefined,
                                    ...(onItemDoubleClick
                                        ? {
                                              '&:focus-visible': {
                                                  outline: '2px solid',
                                                  outlineColor: 'primary.main',
                                                  outlineOffset: -2,
                                              },
                                          }
                                        : {}),
                                }}
                            >
                                {renderItem(item, index)}
                            </Box>
                        );
                    })
                ) : (
                    <Typography
                        variant={'body2'}
                        color={'text.secondary'}
                        sx={{ textAlign: 'center', py: 4 }}
                    >
                        {emptyText}
                    </Typography>
                )}
            </Box>
            {contextItems && contextItems.length > 0 ? (
                <DContextMenu
                    contextMenu={contextMenu}
                    setContextMenu={setContextMenu}
                    contextMenuItems={contextMenuItems}
                />
            ) : null}
        </Box>
    );
}
